import { Auth0Provider } from "@auth0/auth0-react";
import type { MFEConfig } from "@pepperstone/mfe-dev-kit/types";
import { jwtDecode } from "jwt-decode";
import { useCallback, useEffect, useRef, useState } from "react";
import { App } from "@/App";
import {
  clearAccessToken,
  setUnauthorizedHandler,
  updateAccessToken,
} from "@/api";
import { useSessionResumeGuard } from "@/hooks/useSessionResumeGuard";
import { logger } from "@/observability/logger";
import { serializeError } from "@/utils/log";

const TOKEN_REFRESH_BUFFER_MS = 60_000;
const FALLBACK_REFRESH_MS = 4 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 10_000;
const INITIAL_FETCH_MAX_RETRIES = 3;
const INITIAL_FETCH_RETRY_DELAY_MS = 2_000;

// Survives component remounts so the loading screen is never shown after
// the first token has been acquired (e.g. when the host re-renders the MFE).
let cachedToken = "";

export const MfeWrapperApp = (mfe: MFEConfig) => {
  const [token, setToken] = useState(cachedToken);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const dependencies = mfe.dependencies;

  useEffect(() => {
    if (!dependencies) return;

    let cancelled = false;

    const scheduleRefresh = (jwt: string) => {
      clearTimeout(refreshTimeoutRef.current);
      try {
        const { exp } = jwtDecode(jwt);
        if (exp) {
          const refreshIn = exp * 1000 - Date.now() - TOKEN_REFRESH_BUFFER_MS;
          refreshTimeoutRef.current = setTimeout(
            () => fetchToken(true),
            Math.max(refreshIn, MIN_REFRESH_DELAY_MS)
          );
          return;
        }
      } catch {
        /* decode failed, use fallback */
      }
      refreshTimeoutRef.current = setTimeout(
        () => fetchToken(true),
        FALLBACK_REFRESH_MS
      );
    };

    const fetchToken = async (forceRefresh = false, retryCount = 0) => {
      if (cancelled) return;
      try {
        const getToken = dependencies.sessionDomainModel
          .getAccessToken as (params?: {
          refresh?: boolean;
        }) => Promise<string>;
        const accessToken = await getToken(
          forceRefresh ? { refresh: true } : undefined
        );
        if (!cancelled) {
          cachedToken = accessToken;
          if (forceRefresh) {
            updateAccessToken(accessToken);
          } else {
            setToken(accessToken);
          }
          scheduleRefresh(accessToken);
        }
      } catch (error) {
        // Token refresh failure blocks the user from transacting — send it to
        // Datadog as a structured error.
        logger.error("Failed to refresh access token", {
          event: "token_refresh_failed",
          forceRefresh,
          retryCount,
          error: serializeError(error),
        });
        // Retry initial token fetch to recover from transient Auth0 failures
        if (
          !forceRefresh &&
          retryCount < INITIAL_FETCH_MAX_RETRIES &&
          !cancelled
        ) {
          console.warn(
            `Retrying initial token fetch (attempt ${retryCount + 1}/${INITIAL_FETCH_MAX_RETRIES})...`
          );
          setTimeout(
            () => fetchToken(false, retryCount + 1),
            INITIAL_FETCH_RETRY_DELAY_MS
          );
        }
      }
    };

    fetchToken();

    return () => {
      cancelled = true;
      clearTimeout(refreshTimeoutRef.current);
    };
  }, [dependencies]);

  // Re-validate the host session when the page is restored from bfcache or
  // re-shown, so a stale in-memory token can't outlive a logout (see hook docs).
  const verifySession = useCallback(async () => {
    if (!dependencies) return true;
    try {
      return await dependencies.sessionDomainModel.checkIsAuthenticated();
    } catch {
      // Fail-open on transient errors — never log out a valid user.
      return true;
    }
  }, [dependencies]);

  const invalidateSession = useCallback(() => {
    cachedToken = "";
    clearAccessToken();
    setToken("");
    window.location.href = "/";
  }, []);

  useSessionResumeGuard({
    verify: verifySession,
    onInvalid: invalidateSession,
  });

  // A 401 means the token was rejected (expired/revoked/tampered). Reloading or
  // redirecting to "/" does NOT help: on the next load Auth0 re-reads the same
  // bad token from its localStorage cache (getAccessTokenSilently does not
  // re-validate the signature), so the request 401s again → infinite loop.
  // We call login() so the browser leaves the app for Auth0's /authorize flow,
  // which mints a BRAND-NEW valid token (silently, via the SSO session cookie
  // when present) and overwrites the poisoned cache entry — breaking the loop.
  // login() (not logout()) is used so the redirect uses the auth layer's own
  // configured redirect_uri (`${origin}/login/callback`), returning to the
  // correct origin+port on local/staging/prod.
  useEffect(() => {
    if (!dependencies) return;
    setUnauthorizedHandler(() => {
      cachedToken = "";
      clearTimeout(refreshTimeoutRef.current);
      clearAccessToken();
      // Call login() inside the promise chain so a *synchronous* throw is also
      // caught (Promise.resolve(fn()) would evaluate fn() before wrapping it,
      // letting a sync throw escape .catch and skip the fallback).
      void Promise.resolve()
        .then(() => dependencies.sessionDomainModel.login())
        .catch(() => {
          // If login can't be initiated, fall back to a full reload of the app
          // origin, which re-runs the normal auth bootstrap.
          window.location.assign(window.location.origin);
        });
    });
  }, [dependencies]);

  if (!dependencies) {
    console.error("Missing dependencies");
    return null;
  }

  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/login/callback`,
        scope: "openid profile email offline_access",
        audience: "https://api.pepperstone.com",
      }}
      cacheLocation="localstorage"
    >
      <App token={token} locale={dependencies.locale} />
    </Auth0Provider>
  );
};
